import type { ReactNode } from "react";
import {
  TextField as AriaTextField,
  type TextFieldProps as AriaTextFieldProps,
  FieldError,
  Input,
  Label,
  Text,
} from "react-aria-components";
import { cn } from "@core/utils";

interface TextFieldProps extends Omit<
  AriaTextFieldProps,
  "className" | "children"
> {
  /** Accessible label. Always required; hide it visually with `hideLabel`. */
  label: string;
  /** Render the label sr-only (designs show placeholder-only fields, a11y still needs it). */
  hideLabel?: boolean;
  placeholder?: string;
  /** Content rendered at the field's leading edge. */
  leading?: ReactNode;
  /** Content rendered at the field's trailing edge, e.g. a password reveal toggle. */
  trailing?: ReactNode;
  /** Helper copy under the field (design: sm/Regular, text-tertiary). */
  description?: string;
  /** Error text; presence also marks the field invalid. */
  errorMessage?: string;
}

/**
 * Text input primitive (own-the-code) on React Aria's `TextField`.
 * The bordered box is the styled surface; the inner `Input` is transparent so `leading`/
 * `trailing` slots sit on the same row.
 */
export function TextField({
  label,
  hideLabel = false,
  placeholder,
  leading,
  trailing,
  description,
  errorMessage,
  ...props
}: TextFieldProps) {
  const invalid = Boolean(errorMessage) || props.isInvalid;
  const { isReadOnly = false, isDisabled = false } = props;

  return (
    <AriaTextField
      isInvalid={invalid}
      // Our own messages only: with the default (`native`) the browser's wording ("Please include
      // an '@'...") would replace ours for a malformed e-mail, in the browser's language.
      validationBehavior="aria"
      className="flex w-full flex-col gap-1.5"
      {...props}
    >
      <Label
        className={cn(
          "text-sm-medium text-(--text-secondary)",
          hideLabel && "sr-only"
        )}
      >
        {label}
        {/* Decorative — `isRequired` already sets `aria-required` on the input itself, so a screen
            reader would announce this twice if it were not hidden. */}
        {props.isRequired ? (
          <span aria-hidden className="text-(--text-error)">
            {" "}
            *
          </span>
        ) : null}
      </Label>

      {/* Focus is a 2px ink/900 border (tokens.md): the 1px border changes colour and a 1px ring
          of the same colour doubles it, so the box never shifts layout. Read-only reads as a
          filled, quieter field (locked e-mail, cohort code, role: Figma 1077, 1032, 1033). */}
      <div
        className={cn(
          "flex items-center gap-xs rounded-md pl-3.5 pr-3.5 py-2.5",
          "border transition-colors",
          isReadOnly
            ? "bg-surface-field-readonly"
            : isDisabled
              ? "bg-surface-field-disabled"
              : "bg-surface-field shadow-xs",
          invalid
            ? "border-(--border-field-error) focus-within:ring-1 focus-within:ring-(--border-field-error)"
            : isReadOnly
              ? "border-(--border-default) focus-within:border-(--border-field-focus) focus-within:ring-1 focus-within:ring-(--border-field-focus)"
              : "border-(--border-field) focus-within:border-(--border-field-focus) focus-within:ring-1 focus-within:ring-(--border-field-focus)"
        )}
      >
        {leading}
        <Input
          placeholder={placeholder}
          className={cn(
            "min-w-0 flex-1 bg-transparent text-md-regular outline-none",
            isReadOnly ? "text-(--text-secondary)" : "text-(--text-primary)",
            "placeholder:text-(--text-placeholder) disabled:text-(--text-disabled)"
          )}
        />
        {trailing}
      </div>

      {description ? (
        <Text
          slot="description"
          className="text-sm-regular text-(--text-tertiary)"
        >
          {description}
        </Text>
      ) : null}

      <FieldError className="text-sm-regular text-(--text-error)">
        {errorMessage}
      </FieldError>
    </AriaTextField>
  );
}

export type { TextFieldProps };
